import type { ColDef, GridApi } from 'ag-grid-community';
import { logger } from '../services/logger/logger';

/**
 * Shared AG Grid helper utilities for ClientSideAgGrid and ServerSideAgGrid.
 */

const MIN_ROW_HEIGHT = 40;
const MAX_ROW_HEIGHT = 50;

/**
 * Closes all AG Grid filter popups via the API and by removing .ag-popup elements from the DOM.
 * Used by both ClientSideAgGrid and ServerSideAgGrid.
 */
export function closeAllFilterPopups(gridApi: GridApi | null): void {
  try {
    if (gridApi) {
      (gridApi as unknown as { destroyPopups?: () => void }).destroyPopups?.();
    }
  } catch (e) {
    logger.error('Failed to close popups', e as Error);
  }

  try {
    const popups = document.querySelectorAll('.ag-popup');
    popups.forEach((popup) => {
      const popupElement = popup as HTMLElement;
      if (popupElement?.parentNode) {
        popupElement.parentNode.removeChild(popupElement);
      }
    });
  } catch (e) {
    logger.error('Failed to close popups:', e as Error);
  }
}

/**
 * Calculates dynamic row height based on available height and page size.
 * Used by both ClientSideAgGrid and ServerSideAgGrid for consistent row sizing.
 */
export function getDynamicRowHeight(availableHeight: number, pageSize: number): number {
  let calculatedHeight: number;
  if (pageSize === 10) {
    calculatedHeight = Math.floor(availableHeight / pageSize);
  } else if (pageSize === 25) {
    calculatedHeight = Math.floor(availableHeight / pageSize) + 30;
  } else if (pageSize === 50) {
    calculatedHeight = Math.floor(availableHeight / pageSize) + 20;
  } else if (pageSize === 100) {
    calculatedHeight = Math.floor(availableHeight / pageSize) + 10;
  } else {
    calculatedHeight = Math.floor(availableHeight / pageSize) + 30;
  }

  return Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, calculatedHeight));
}

/**
 * Returns responsive header height based on current viewport width.
 * Used by both ClientSideAgGrid and ServerSideAgGrid for layout calculations.
 */
export function getResponsiveHeaderHeight(): number {
  const screenWidth = window.innerWidth;
  if (screenWidth >= 1920) return 42;
  if (screenWidth >= 1440) return 40;
  if (screenWidth >= 1024) return 38;
  if (screenWidth >= 768) return 36;
  return 34;
}

/**
 * Returns responsive font size based on current viewport width.
 * Used by both ClientSideAgGrid and ServerSideAgGrid for consistent typography.
 */
export function getResponsiveFontSize(): string {
  const w = window.innerWidth;
  if (w >= 1920) return '1rem';
  if (w >= 1440) return '0.9375rem';
  if (w >= 1024) return '0.875rem';
  if (w >= 768) return '0.8125rem';
  return '0.75rem';
}

type MapColumnDefsOptions = {
  applyFlexSizing: boolean;
  tooltipComponent: any;
  containerHeight: number;
  suppressHeaderFocus?: boolean;
  suppressReactComponentReloading?: boolean;
  suppressKeyboardEvent?: (_params: any) => boolean;
  getTooltipWidth?: (_args: {
    width: unknown;
    rest: Record<string, unknown>;
    defaultWidth: number;
  }) => number;
  defaultWidth?: number;
};

/**
 * Calculates total min width for a set of column definitions.
 */
export function getTotalMinWidth(columnDefs: ColDef[] = []): number {
  const DEFAULT_MIN = 120;
  return columnDefs.reduce((sum: number, col: any) => {
    const minW =
      typeof col?.minWidth === 'number'
        ? col.minWidth
        : typeof col?.width === 'number'
          ? col.width
          : DEFAULT_MIN;
    return sum + (minW > 0 ? minW : DEFAULT_MIN);
  }, 0);
}

/**
 * Applies shared column defaults + tooltip behavior to AG Grid columns.
 */
export function mapGridColumnDefs(
  columnDefs: ColDef[] = [],
  options: MapColumnDefsOptions,
): ColDef[] {
  const {
    applyFlexSizing,
    tooltipComponent,
    containerHeight,
    suppressHeaderFocus,
    suppressReactComponentReloading,
    suppressKeyboardEvent,
    getTooltipWidth,
    defaultWidth = 120,
  } = options;

  return columnDefs.map((itm) => {
    const { width, ...rest } = (itm as any) || {};

    const showTooltip = (rest as any)?.showTooltip;
    const shouldShowTooltip = showTooltip === true || showTooltip !== false;

    const colWidth = getTooltipWidth
      ? getTooltipWidth({ width, rest, defaultWidth })
      : typeof width === 'number'
        ? width
        : (rest as any)?.minWidth || defaultWidth;
    const avgCharWidth = 7;
    const cellPadding = 24;
    const calculatedThreshold = Math.floor((colWidth - cellPadding) / avgCharWidth);
    const tooltipThreshold = (rest as any)?.tooltipThreshold ?? calculatedThreshold;

    return {
      resizable: true,
      suppressMovable: true,
      ...(suppressHeaderFocus ? { suppressHeaderFocus: true } : {}),
      ...(suppressReactComponentReloading ? { suppressReactComponentReloading: true } : {}),
      ...(suppressKeyboardEvent ? { suppressKeyboardEvent } : {}),
      flex: applyFlexSizing ? ((rest as any)?.flex ?? 1) : undefined,
      ...(typeof width === 'number' ? { minWidth: width } : {}),
      ...(!width && (rest as any)?.minWidth == null ? { minWidth: defaultWidth } : {}),
      ...rest,
      ...(shouldShowTooltip
        ? {
            tooltipComponent,
            tooltipValueGetter: ({ value }: { value: unknown }) => {
              const stringValue = value != null ? String(value) : '';
              return stringValue.length > tooltipThreshold ? stringValue : null;
            },
            tooltipComponentParams: (params: any) => ({
              value: params.value,
              colDef: params.colDef,
              context: { containerHeight },
            }),
          }
        : {}),
    } as ColDef;
  });
}
